/* eslint-disable */
import { useEffect, useState, useMemo } from "react";
import { Search, LogOut } from "lucide-react";
import { villaApi } from "../auth/api";
import "./OnboardingVilla.css";

const ROLE_MAP = {
  primary: "PRIMARY_OWNER",
  shared: "SHARED_OWNER",
  resident: "RESIDENT",
  tenant: "TENANT",
};

const ROLE_OPTIONS = [
  {
    key: "primary",
    title: "Primary Owner",
    sub: "I own the villa",
    note: "Will be reviewed by admin",
  },
  {
    key: "shared",
    title: "Shared Owner",
    sub: "Co-owner on the sale deed",
    note: "Will be reviewed by admin",
  },
  {
    key: "resident",
    title: "Resident",
    sub: "Family member or household resident",
    note: "Will be reviewed by villa owner",
  },
  {
    key: "tenant",
    title: "Tenant",
    sub: "Currently renting",
    note: "Tenancy end date required",
  },
];

export default function OnboardingVilla({ userName, onSubmitted, onLogout }) {
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedVilla, setSelectedVilla] = useState(null);
  const [role, setRole] = useState("primary");
  const [relation, setRelation] = useState("");
  const [tenantEndDate, setTenantEndDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    villaApi
      .list(token)
      .then((vs) => {
        setVillas(vs || []);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return villas;
    const q = search.toLowerCase();
    return villas.filter((v) => v.villa_number.toLowerCase().includes(q));
  }, [villas, search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selectedVilla) return setError("Please select a villa");
    if (role === "tenant" && !tenantEndDate) {
      return setError("Please provide a tenancy end date");
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("access_token");
      await villaApi.requestAccess(
        {
          villa_id: selectedVilla.id,
          role: ROLE_MAP[role],
          relation: relation || null,
          tenant_end_date: role === "tenant" ? tenantEndDate : null,
        },
        token
      );
      onSubmitted && onSubmitted();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="onb-shell">
      <div className="onb-topbar">
        <div className="onb-brand">Sanjivini Connect</div>
        <button className="onb-logout" onClick={onLogout}>
          <LogOut size={16} /> Sign out
        </button>
      </div>

      <div className="onb-content">
        <div className="onb-card">
          <div className="onb-head">
            <h1>Almost there, {userName?.split(" ")[0] || "there"}</h1>
            <p>
              Tell us which villa you belong to. Your account becomes active
              once the board approves your request.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="onb-field">
              <label>Which villa? <span className="req">*</span></label>
              <div className="onb-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search by villa number (e.g. Villa-12)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="onb-villa-list">
                {loading ? (
                  <div className="onb-empty">Loading villas…</div>
                ) : filtered.length === 0 ? (
                  <div className="onb-empty">No villas match</div>
                ) : (
                  filtered.slice(0, 80).map((v) => (
                    <button
                      type="button"
                      key={v.id}
                      className={`onb-villa-item ${
                        selectedVilla?.id === v.id ? "selected" : ""
                      }`}
                      onClick={() => setSelectedVilla(v)}
                    >
                      <strong>{v.villa_number}</strong>
                      <span>{v.address || "Tukkuguda"}</span>
                    </button>
                  ))
                )}
              </div>
              {selectedVilla && (
                <div className="onb-selected">
                  Selected: <strong>{selectedVilla.villa_number}</strong>
                </div>
              )}
            </div>

            <div className="onb-field">
              <label>Your role <span className="req">*</span></label>
              <div className="onb-roles">
                {ROLE_OPTIONS.map((r) => (
                  <button
                    type="button"
                    key={r.key}
                    className={`onb-role ${role === r.key ? "selected" : ""}`}
                    onClick={() => setRole(r.key)}
                  >
                    <div className="onb-role-radio" />
                    <div className="onb-role-text">
                      <div className="onb-role-title">{r.title}</div>
                      <div className="onb-role-sub">{r.sub}</div>
                      <div className="onb-role-note">{r.note}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {role === "resident" && (
              <div className="onb-field">
                <label>Relation to owner</label>
                <input
                  type="text"
                  className="onb-input"
                  value={relation}
                  onChange={(e) => setRelation(e.target.value)}
                  placeholder="e.g. Spouse, Son, Daughter"
                />
              </div>
            )}

            {role === "tenant" && (
              <div className="onb-field">
                <label>Tenancy end date <span className="req">*</span></label>
                <input
                  type="date"
                  className="onb-input"
                  value={tenantEndDate}
                  onChange={(e) => setTenantEndDate(e.target.value)}
                />
              </div>
            )}

            {error && <div className="onb-error">{error}</div>}

            <button
              type="submit"
              className="onb-submit"
              disabled={submitting || !selectedVilla}
            >
              {submitting ? "Submitting…" : "Submit request"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
